import { useRef, useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { motion } from 'framer-motion';
import {
  Pencil,
  Eraser,
  Trash2,
  Undo,
  Redo,
  Download
} from 'lucide-react';

export interface EnhancedDrawingCanvasRef {
  getDataUrl: () => string;
  clear: () => void;
}

interface EnhancedDrawingCanvasProps {
  language: 'en' | 'es';
}

// Organized color palettes with green-friendly options
const colorPalettes = [
  {
    label: { en: 'Emerald Greens', es: 'Verdes Esmeralda' },
    colors: ['#059669', '#10b981', '#34d399', '#6ee7b7', '#047857', '#065f46', '#064e3b', '#022c22']
  },
  {
    label: { en: 'Forest & Meadow', es: 'Bosque y Pradera' },
    colors: ['#2E8B57', '#228B22', '#32CD32', '#90EE90', '#1B5E20', '#4CAF50', '#8BC34A', '#CDDC39']
  },
  {
    label: { en: 'Teal & Aqua', es: 'Verde Azulado y Agua' },
    colors: ['#00ACC1', '#26C6DA', '#4DD0E1', '#0891b2', '#06b6d4', '#22d3ee', '#67e8f9', '#a5f3fc']
  },
  {
    label: { en: 'Ocean Blues', es: 'Azules Océano' },
    colors: ['#0D47A1', '#1976D2', '#42A5F5', '#90CAF9', '#1e40af', '#3b82f6', '#60a5fa', '#93c5fd']
  },
  {
    label: { en: 'Warm Accents', es: 'Acentos Cálidos' },
    colors: ['#F97316', '#FB923C', '#FFA726', '#FFEB3B', '#FDD835', '#FFEE58', '#FF5252', '#F44336']
  },
  {
    label: { en: 'Neutrals & Basics', es: 'Neutrales y Básicos' },
    colors: ['#9E9E9E', '#757575', '#616161', '#424242', '#000000', '#FFFFFF']
  }
];

export const EnhancedDrawingCanvas = forwardRef<EnhancedDrawingCanvasRef, EnhancedDrawingCanvasProps>(
  ({ language }, ref) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [color, setColor] = useState('#059669'); // Start with a vibrant emerald green
    const [brushSize, setBrushSize] = useState(5);
    const [tool, setTool] = useState<'brush' | 'eraser'>('brush');

    // Drawing state for undo/redo
    const [history, setHistory] = useState<string[]>([]);
    const [historyStep, setHistoryStep] = useState(-1);

    const text = {
      en: {
        brush: 'Brush',
        eraser: 'Eraser',
        clear: 'Clear',
        undo: 'Undo',
        redo: 'Redo',
        download: 'Download',
        brushSize: 'Brush Size',
        colors: 'Colors'
      },
      es: {
        brush: 'Pincel',
        eraser: 'Borrador',
        clear: 'Limpiar',
        undo: 'Deshacer',
        redo: 'Rehacer',
        download: 'Descargar',
        brushSize: 'Tamaño del Pincel',
        colors: 'Colores'
      }
    };

    const t = text[language];

    useImperativeHandle(ref, () => ({
      getDataUrl: () => {
        if (!canvasRef.current) return '';
        return canvasRef.current.toDataURL('image/png');
      },
      clear: () => {
        clearCanvas();
      }
    }));

    // Initialize canvas
    useEffect(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      // Resize canvas
      const resizeCanvas = () => {
        const container = canvas.parentElement;
        if (!container) return;

        // Save current drawing
        const tempCanvas = document.createElement('canvas');
        const tempCtx = tempCanvas.getContext('2d');
        if (!tempCtx) return;

        tempCanvas.width = canvas.width;
        tempCanvas.height = canvas.height;
        tempCtx.drawImage(canvas, 0, 0);

        // Resize
        const containerWidth = container.clientWidth;
        canvas.width = containerWidth;
        canvas.height = containerWidth * 0.75; // 4:3 aspect ratio

        // Fill with white background
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Restore drawing
        ctx.drawImage(tempCanvas, 0, 0, canvas.width, canvas.height);
      };

      resizeCanvas();
      window.addEventListener('resize', resizeCanvas);

      // Save initial state
      saveToHistory();

      return () => window.removeEventListener('resize', resizeCanvas);
    }, []);

    const saveToHistory = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const dataUrl = canvas.toDataURL();
      const newHistory = history.slice(0, historyStep + 1);
      newHistory.push(dataUrl);
      setHistory(newHistory);
      setHistoryStep(newHistory.length - 1);
    };

    const undo = () => {
      if (historyStep > 0) {
        const newStep = historyStep - 1;
        setHistoryStep(newStep);
        loadFromHistory(newStep);
      }
    };

    const redo = () => {
      if (historyStep < history.length - 1) {
        const newStep = historyStep + 1;
        setHistoryStep(newStep);
        loadFromHistory(newStep);
      }
    };

    const loadFromHistory = (step: number) => {
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext('2d');
      if (!canvas || !ctx) return;

      const img = new Image();
      img.src = history[step];
      img.onload = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
      };
    };

    const clearCanvas = () => {
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext('2d');
      if (!canvas || !ctx) return;

      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      saveToHistory();
    };

    const downloadDrawing = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const link = document.createElement('a');
      link.download = `wildlife-power-mixer-${Date.now()}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    };

    const getCanvasCoordinates = (
      e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>,
      canvas: HTMLCanvasElement
    ) => {
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;

      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
      const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;

      return {
        x: (clientX - rect.left) * scaleX,
        y: (clientY - rect.top) * scaleY
      };
    };

    const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext('2d');
      if (!canvas || !ctx) return;

      setIsDrawing(true);

      const { x, y } = getCanvasCoordinates(e, canvas);

      ctx.beginPath();
      ctx.moveTo(x, y);
    };

    const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
      if (!isDrawing) return;

      const canvas = canvasRef.current;
      const ctx = canvas?.getContext('2d');
      if (!canvas || !ctx) return;

      const { x, y } = getCanvasCoordinates(e, canvas);

      ctx.strokeStyle = tool === 'eraser' ? '#ffffff' : color;
      ctx.lineWidth = brushSize;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      ctx.lineTo(x, y);
      ctx.stroke();
    };

    const stopDrawing = () => {
      if (!isDrawing) return;
      setIsDrawing(false);
      saveToHistory();
    };

    return (
      <div className="space-y-4">
        {/* Canvas */}
        <div className="bg-white rounded-xl shadow-inner border-4 border-purple-200">
          <canvas
            ref={canvasRef}
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            onTouchStart={startDrawing}
            onTouchMove={draw}
            onTouchEnd={stopDrawing}
            className="w-full cursor-crosshair touch-none rounded-lg"
            style={{ aspectRatio: '4/3' }}
          />
        </div>

        {/* Tool Controls */}
        <div className="flex gap-2 flex-wrap justify-center">
          <Button
            onClick={() => setTool('brush')}
            variant={tool === 'brush' ? 'default' : 'outline'}
            size="sm"
            className="gap-2"
          >
            <Pencil className="w-4 h-4" />
            {t.brush}
          </Button>
          <Button
            onClick={() => setTool('eraser')}
            variant={tool === 'eraser' ? 'default' : 'outline'}
            size="sm"
            className="gap-2"
          >
            <Eraser className="w-4 h-4" />
            {t.eraser}
          </Button>
          <Button
            onClick={undo}
            disabled={historyStep <= 0}
            variant="outline"
            size="sm"
            className="gap-2"
          >
            <Undo className="w-4 h-4" />
            {t.undo}
          </Button>
          <Button
            onClick={redo}
            disabled={historyStep >= history.length - 1}
            variant="outline"
            size="sm"
            className="gap-2"
          >
            <Redo className="w-4 h-4" />
            {t.redo}
          </Button>
          <Button
            onClick={clearCanvas}
            variant="outline"
            size="sm"
            className="gap-2"
          >
            <Trash2 className="w-4 h-4" />
            {t.clear}
          </Button>
          <Button
            onClick={downloadDrawing}
            variant="outline"
            size="sm"
            className="gap-2"
          >
            <Download className="w-4 h-4" />
            {t.download}
          </Button>
        </div>

        {/* Brush Size Slider */}
        <div className="bg-white/90 backdrop-blur-sm rounded-lg p-4 shadow-md border border-gray-200">
          <label className="text-lg font-medium text-gray-700 mb-2 block">
            {t.brushSize}: {brushSize}px
          </label>
          <Slider
            value={[brushSize]}
            onValueChange={(value) => setBrushSize(value[0])}
            min={1}
            max={30}
            step={1}
            className="w-full"
          />
        </div>

        {/* Color Palettes */}
        <div className="bg-white/90 backdrop-blur-sm rounded-lg p-4 shadow-md border border-gray-200 space-y-3">
          <h3 className="text-lg font-medium text-gray-700">{t.colors}</h3>
          <div className="space-y-3 max-h-[40vh] overflow-y-auto pr-1">
            {colorPalettes.map((palette, paletteIndex) => (
              <div key={paletteIndex}>
                <p className="text-base text-gray-500 mb-2">{palette.label[language]}</p>
                <div className="grid grid-cols-8 gap-2">
                  {palette.colors.map((c) => (
                    <motion.button
                      key={c}
                      onClick={() => setColor(c)}
                      whileTap={{ scale: 0.85 }}
                      whileHover={{ scale: 1.1 }}
                      className={`w-full aspect-square rounded-md border-2 transition-all ${
                        color === c
                          ? 'border-purple-500 ring-2 ring-purple-300 scale-105'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                      style={{ backgroundColor: c }}
                      aria-label={c}
                      title={c}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }
);

EnhancedDrawingCanvas.displayName = 'EnhancedDrawingCanvas';
